import { useRef } from "react";
import type React from "react";
import { useTranslation } from "react-i18next";
import { useScrollAnimation, useScrollAnimationMultiple } from "@/hooks/useScrollAnimation";

export default function AboutSection() {
  const { t } = useTranslation();
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: photoRef, isVisible: photoVisible } = useScrollAnimation();
  const { setRef, visibleItems } = useScrollAnimationMultiple(3, { threshold: 0.1 });

  return (
    <section id="about" className="py-16 sm:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-10 sm:mb-16 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 leading-tight" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            {t("about_heading")}
          </h2>
        </div>

        <div className="flex flex-col lg:flex-row items-start gap-10 sm:gap-16">
          <div
            ref={photoRef as React.RefObject<HTMLDivElement>}
            className={`w-full lg:w-5/12 flex-shrink-0 transition-all ease-out ${photoVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"}`}
            style={{ transitionDuration: "800ms" }}
          >
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden aspect-[4/5] w-full max-w-[220px] sm:max-w-sm mx-auto lg:mx-0">
                <img
                  src="https://static.readdy.ai/image/325bfa907bf4dc0512d8145a4cdf2d5c/de96ff51c0041b280e97e1e45aab3b86.jpeg"
                  alt="てんてんおじさん 伊計島にて"
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent px-6 py-5" />
              </div>
            </div>
          </div>

          <div className="w-full lg:w-7/12 space-y-8 sm:space-y-10">
            <div
              ref={setRef(0) as unknown as React.RefObject<HTMLDivElement>}
              className={`transition-all duration-700 ease-out ${visibleItems[0] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
            >
              <p
                className="text-gray-600 text-base leading-loose"
                dangerouslySetInnerHTML={{ __html: t("about_intro").replace(/\n/g, "<br />") }}
              />
            </div>

            <div
              ref={setRef(1) as unknown as React.RefObject<HTMLDivElement>}
              className={`border-l-4 border-[#0ABAB5] pl-6 transition-all duration-700 ease-out ${visibleItems[1] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              style={{ transitionDelay: visibleItems[1] ? "100ms" : "0ms" }}
            >
              <p className="text-xs text-[#0ABAB5] font-bold tracking-widest mb-3">{t("about_block1_label")}</p>
              <p
                className="text-gray-600 text-sm leading-loose"
                dangerouslySetInnerHTML={{ __html: t("about_block1_text").replace(/\n/g, "<br />") }}
              />
              <blockquote className="mt-4 text-gray-500 text-sm leading-relaxed italic bg-white rounded-xl px-5 py-4 border border-gray-100">
                {t("about_block1_quote")}
              </blockquote>
              <p className="text-gray-500 text-sm leading-loose mt-3">{t("about_block1_sub")}</p>
            </div>

            <div
              ref={setRef(2) as unknown as React.RefObject<HTMLDivElement>}
              className={`border-l-4 border-[#0ABAB5]/40 pl-6 transition-all duration-700 ease-out ${visibleItems[2] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              style={{ transitionDelay: visibleItems[2] ? "200ms" : "0ms" }}
            >
              <p className="text-xs text-[#0ABAB5] font-bold tracking-widest mb-3">{t("about_block2_label")}</p>
              <p className="text-gray-600 text-sm leading-loose">{t("about_block2_text")}</p>
              <p
                className="text-gray-600 text-sm leading-loose mt-3"
                dangerouslySetInnerHTML={{ __html: t("about_block2_text2") }}
              />
              <p
                className="text-gray-500 text-sm leading-loose mt-3"
                dangerouslySetInnerHTML={{ __html: t("about_block2_sub").replace(/\n/g, "<br />") }}
              />
            </div>

            <div
              className={`border-l-4 border-[#0ABAB5]/40 pl-6 transition-all duration-700 ease-out ${visibleItems[2] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              style={{ transitionDelay: visibleItems[2] ? "350ms" : "0ms" }}
            >
              <p className="text-xs text-[#0ABAB5] font-bold tracking-widest mb-3">{t("about_block3_label")}</p>
              <p
                className="text-gray-600 text-sm leading-loose"
                dangerouslySetInnerHTML={{ __html: t("about_block3_text").replace(/\n/g, "<br />") }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
